import {
    ChecklistItemCategory,
    INeighbourhood,
    KeySnapshot,
    NewJoiner,
    NewJoinerChecklistItem,
    NewJoinerChecklistItemFilter,
    NewJoinerForm,
    NewJoinerType,
    UNASSIGNED_NEIGHBOURHOOD_ID
} from "../../api/Model";
import {HierarchyEmployee} from "../../util/EmployeeHierarchy";
import {ResolvedNewJoiner} from "../../api/ResolvedNewJoinerStore";
import {mapNotNull} from "../../util/ArrayUtil";
import {Opt} from "../../util/Optional";
import {toNewJoinerForm} from "../../api/Projections";

export interface NewJoinerOperations {
    keys: KeySnapshot[];
    neighbourhoods: INeighbourhood[];
    handleEdit?: () => void;
    handleDelete?: () => void;
    handleSelectNeighbourhoodClick?: () => void;
    handleClearNeighbourhoodClick?: () => Promise<void>;
    canOperateKeys: boolean;
    checklistToggles: Record<ChecklistItemCategory, {
        item: NewJoinerChecklistItem;
        checked: boolean;
        disabled?: boolean;
        toggle: () => any;
    }[]>;
}

export type NewJoinerLinkPlaceholder = {
    pattern: string;
    replace: (newJoiner: ResolvedNewJoiner) => string | null;
    description: string;
};

export const newJoinerLinkPlaceholders: NewJoinerLinkPlaceholder[] = [
    {
        pattern: '{hibob-profile-id}',
        replace: newJoiner => newJoiner.employee?.hibobId ?? null,
        description: 'Hibob Profile ID',
    },
    {
        pattern: '{email}',
        replace: newJoiner => newJoiner.employee?.email ?? null,
        description: 'Email',
    }
];

export const newJoinerChecklistItemFilterGroup: Record<NewJoinerChecklistItemFilter, {
    group: 'work', remote: boolean
} | {
    group: 'type', type: NewJoinerType
}> = {
    [NewJoinerChecklistItemFilter.Remote]: {group: 'work', remote: true},
    [NewJoinerChecklistItemFilter.Office]: {group: 'work', remote: false},
    [NewJoinerChecklistItemFilter.NewFTE]: {group: 'type', type: NewJoinerType.NewFTE},
    [NewJoinerChecklistItemFilter.NewIntern]: {group: 'type', type: NewJoinerType.NewIntern},
    [NewJoinerChecklistItemFilter.InternToFTE]: {group: 'type', type: NewJoinerType.InternToFTE},
    [NewJoinerChecklistItemFilter.ReturnFromParentalLeave]: {
        group: 'type',
        type: NewJoinerType.ReturnFromParentalLeave
    },
    [NewJoinerChecklistItemFilter.InternationalRelocation]: {
        group: 'type',
        type: NewJoinerType.InternationalRelocation
    },
    [NewJoinerChecklistItemFilter.LocalRelocation]: {group: 'type', type: NewJoinerType.LocalRelocation},
};

class NewJoinerMatcher {
    private workFilters: Set<boolean>;
    private typeFilters: Set<NewJoinerType>;

    constructor(filters: NewJoinerChecklistItemFilter[]) {
        this.workFilters = new Set(mapNotNull(filters, filter => {
            const g = newJoinerChecklistItemFilterGroup[filter];
            return g.group === 'work' ? g.remote : undefined;
        }));
        this.typeFilters = new Set(mapNotNull(filters, filter => {
            const g = newJoinerChecklistItemFilterGroup[filter];
            return g.group === 'type' ? g.type : undefined;
        }));
    }

    matches(newJoiner: ResolvedNewJoiner) {
        if (this.workFilters.size > 0) {
            if (!this.workFilters.has(newJoiner.remote))
                return false;
        }

        if (this.typeFilters.size > 0) {
            if (!this.typeFilters.has(newJoiner.type)) {
                return false;
            }
        }

        return true;
    }
}

export class NewJoinerApi {
    readonly newJoinerMatcher: Map<NewJoinerChecklistItem['id'], NewJoinerMatcher>;

    constructor(
        readonly onEdit: (newJoiner: NewJoiner) => void,
        readonly onDelete: (newJoiner: NewJoiner) => void,
        readonly updateNewJoiner: (newJoiner: NewJoiner, form: NewJoinerForm) => Promise<NewJoiner>,
        readonly onSelectNeighbourhood: (employee: HierarchyEmployee) => void,
        readonly removeEmployeeFromNeighbourhood: (newJoiner: ResolvedNewJoiner) => Promise<void>,
        readonly newJoinerChecklistItems: NewJoinerChecklistItem[],
        readonly canAdmin: boolean,
        readonly canOperateKeys: boolean,
        readonly canManageNeighbourhoods: boolean,
    ) {
        this.newJoinerMatcher = new Map(
            this.newJoinerChecklistItems
                .map(it => [it.id, new NewJoinerMatcher(it.filters ?? [])])
        );
    }

    forNewJoiner(newJoiner: ResolvedNewJoiner): NewJoinerOperations {
        const employee = newJoiner.employee;
        const keys = employee?.keys ?? [];
        const neighbourhoods = employee?.neighbourhoods?.filter(it => it.id !== UNASSIGNED_NEIGHBOURHOOD_ID) ?? [];

        const checklistToggles = this.newJoinerChecklistItems
            .filter(checklistItem => {
                const matcher = this.newJoinerMatcher.get(checklistItem.id);
                return matcher ? matcher.matches(newJoiner) : true;
            })
            .map(checklistItem => ({
                item: {
                    ...checklistItem,
                    link: NewJoinerApi.rewriteLinks(checklistItem.link, newJoiner),
                },
                checked: newJoiner.checklist[checklistItem.id] ?? false,
                disabled: !this.canAdmin,
                toggle: () => this.canAdmin ? this.toggleCheckItem(newJoiner, checklistItem.id) : undefined,
            }));

        return {
            keys,
            neighbourhoods,
            handleEdit: this.canAdmin ? () => this.onEdit(newJoiner) : undefined,
            handleDelete: this.canAdmin ? () => this.onDelete(newJoiner) : undefined,
            handleSelectNeighbourhoodClick: this.canManageNeighbourhoods && employee
                ? () => this.onSelectNeighbourhood(employee)
                : undefined,
            handleClearNeighbourhoodClick: this.canManageNeighbourhoods && neighbourhoods.length > 0
                ? () => this.removeEmployeeFromNeighbourhood(newJoiner)
                : undefined,
            canOperateKeys: this.canOperateKeys,
            checklistToggles: {
                [ChecklistItemCategory.Neighbourhood]: checklistToggles.filter(toggle => toggle.item.category === ChecklistItemCategory.Neighbourhood),
                [ChecklistItemCategory.Key]: checklistToggles.filter(toggle => toggle.item.category === ChecklistItemCategory.Key),
                [ChecklistItemCategory.WelcomePack]: checklistToggles.filter(toggle => toggle.item.category === ChecklistItemCategory.WelcomePack),
                [ChecklistItemCategory.Standalone]: checklistToggles.filter(toggle => toggle.item.category === ChecklistItemCategory.Standalone),
            }
        };
    }

    private static rewriteLinks(link: Opt<string>, newJoiner: ResolvedNewJoiner): Opt<string> {
        if (!link) return link;

        let resolvedLink = link;
        for (const placeholder of newJoinerLinkPlaceholders) {
            if (resolvedLink.includes(placeholder.pattern)) {
                const replacement = placeholder.replace(newJoiner);
                if (replacement === null) {
                    return null;
                } else {
                    resolvedLink = resolvedLink.replace(placeholder.pattern, replacement);
                }
            }
        }
        return resolvedLink;
    }

    private toggleCheckItem(newJoiner: NewJoiner, itemId: number): Promise<NewJoiner> {
        return this.updateNewJoiner(newJoiner, {
            ...toNewJoinerForm(newJoiner),
            checklist: {
                ...newJoiner.checklist,
                [itemId]: !newJoiner.checklist[itemId],
            },
        });
    }
}
